import React, { useEffect, useRef, useState } from "react";
import { ModalConstructor } from "../../Modals/ModalConstructor";
import {
    BoldText,
    CheckboxFormControlLabel,
    ErrorContainer,
    FormContainerStack,
    FormHeader,
    FormImageInputStack,
    ImagesFormControlLabel,
    InputsContainer,
    InputsContainerTitleTypography,
    IsQuestionCheckbox,
    LoadingStack,
    OptionsContainer,
    OptionsIconButton,
    PostFormHeaderContainer,
    PostTextOutlinedInput,
    SubmitButton,
    SubmitStack,
    TabsStyled,
} from "./PostForm.styles";
import { LocationType } from "../../SearchHeader/ListItem/ListItemLocation.types";
import { POST_CREATION_TARGET, PostImage } from "../Post.types";
import { Box, ButtonProps, IconButton, Stack, Typography } from "@mui/material";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import { getPoi } from "@infrastructure/Services/PoiService";
import { PointOfInterestResponse } from "../../Group/PointsOfInterest/PointsOfInterest.types";
import {
    postGroupPost,
    putGroupPost,
} from "@infrastructure/Services/GroupService";
import {
    RESPONSE_CODE_INAPPROPRIATE_TEXT,
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_NOT_AUTHORIZED,
    RESPONSE_CODE_PROFILE_SUSPENDED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import {
    ErrorState,
    useErrorStore,
} from "../../Modals/ServiceModals/GeneralErrorsModal/GlobalState";
import { GroupTypeEnum } from "../../../Pages/GroupDetailsPage/Group.types";
import { ImagesPreview } from "./ImagesPreview";
import { ImageSolidIcon } from "@icons/ImageSolidIcon";
import { LocationDotSolidIcon } from "@icons/LocationDotSolidIcon";
import { LinkSolidIcon } from "@icons/LinkSolidIcon";
import { LocationSearchTab } from "./LocationSearchTab/LocationSearchTab";
import { PostFormInfoText } from "./PostFormInfoText";
import { PostFormHeader } from "./PostFormHeader";
import {
    createLocationPost,
    extractLocationId,
} from "@infrastructure/Services/PostService";
import { GOOGLE_PLACE_PREFIX } from "../../Group/PointsOfInterest/PointsOfInterest.service";
import {
    MEDIA_TYPE,
    POST_FORM_CONTENT_INPUT,
    POST_FORM_TABS,
    PostFormModalType,
    UploadImagesInput,
} from "./PostFormModal.types";
import {
    CommunityCircularProgress,
    PostFormInputTriggerButton,
} from "@shared/styles";
import { formatLink, isValidUrl } from "@infrastructure/Helpers/UrlHelper";
import { PostCreationErrorModal } from "../../Modals/ServiceModals/PostCreationErrorModal";
import { useUploadPostCommentImages } from "../../Modals/CommentFormModal/useUploadPostCommentImages";
import { HeadlinePostInputButton } from "../../../Pages/GroupDetailsPage/Group.styles";
import { useMediaFilesUploadInputStore } from "../../MediaFilesUploadInput/MediaFilesUploadInput.store";
import { MediaFilesUploadInput } from "../../MediaFilesUploadInput/MediaFilesUploadInput";
import { usePostFormModalStore } from "../../../Pages/GroupDetailsPage/Stores/PostFormModalStore";
import { LinkoutFieldTab } from "./LinkoutFieldTab";
import { SelectedLinkoutPreview } from "./SelectedLinkoutPreview";
import { addPxPostfix } from "@shared/addPxPostfix";
import { PlaySolidIcon } from "@icons/PlaySolidIcon";
import { InvalidMediaUploadModal } from "../../Modals/ServiceModals/InvalidMediaUploadModal/InvalidMediaUploadModal";
import { publish } from "@infrastructure/Events/EventBus";
import { POST_SUBMITTED } from "./events";

import { usePollOptionsState } from "./PollTab/PollOptionsState";
import { PollTab } from "./PollTab/PollTab";
import { PostPollOptionsPreview } from "./PostPollOptionsPreview";
import { SquarePollSolidIcon } from "@icons/SquarePollSolidIcon";
import dayjs from "dayjs";
import { ArrowLeftIcon } from "@icons/ArrowLeft";
import { PollTypesEnum } from "@shared/Types/Polls";
import { useIsPollsValid } from "./PollTab/useIsPollsValid";
import { usePostFormPollMediaFilesUploadInputStore } from "./PollTab/PollsTabSections/PostFormPollMediaFilesUploadInput.store";
import { useGroupSelector } from "../../GroupSelector/useGroupSelector";
import { useLinkout } from "@infrastructure/Hooks/useLinkout";
import { usePostFormConfig } from "./usePostFormConfig";
import { UserConversationListFacade } from "./UserConversationListFacade";

export function PostFormModal({
    groupType,
    postFormSubmissionCallback,
    data,
    initialUserLocation,
    setInitialUserLocation,
    postCreationTarget = POST_CREATION_TARGET.GROUP,
    postFormModalCloseCallback,
}: PostFormModalType) {
    const {
        isPostFormModalOpen,
        setIsPostFormModalOpen,
        postFormOpeningTarget,
        newPostSourceType,
    } = usePostFormModalStore();

    const abortController = useRef<AbortController>(new AbortController());

    const linkoutRef = useRef<HTMLInputElement>();

    const handleClose = () => {
        postFormModalCloseCallback?.();
        setPostText("");
        setLinkoutUrl("");
        setIsQuestion(false);
        setUserLocation(undefined);
        setImages([]);
        setInitialUserLocation?.(undefined);
        setIsPostFormModalOpen(false);
        setTabIndex(POST_FORM_TABS.DEFAULT);
        setActivateVideo(false);
        setIsPostTextValid(false);
        setSelectedConversations([]);
        setPollImages([]);
        abortController.current.abort();
        abortController.current = new AbortController();
        clearPollOptions();
        setAllowPollEdit(true);
    };

    const [tabIndex, setTabIndex] = useState(POST_FORM_TABS.DEFAULT);
    const [postText, setPostText] = useState<string>("");
    const [isPostTextValid, setIsPostTextValid] = useState(false);
    const { linkoutUrl, setLinkoutUrl, clearLinkout } = useLinkout(
        postText,
        data?.postData?.linkoutUrl || "",
    );
    const {
        closedAt,
        setClosedAt,
        addPollOption,
        clearPollOptions,
        pollOptions,
        updatePollType,
        pollType,
    } = usePollOptionsState();
    const isPollOptionsValid = useIsPollsValid();
    const [isQuestion, setIsQuestion] = useState<boolean>(false);
    const [userLocation, setUserLocation] = useState<
        LocationType | undefined
    >();
    const [formActive, setFormActive] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean | undefined>(
        undefined,
    );

    const [isPostCreationErrorModalOpen, setPostCreationErrorModalOpen] =
        useState(false);

    const {
        addLinkEnabled,
        addVideoEnabled,
        addLocationEnabled,
        createPostPlaceholder,
        addPollEnabled,
    } = usePostFormConfig();

    const showLinkoutInput = addLinkEnabled;
    const inputFileRef = useRef<HTMLInputElement>(null);
    const inputVideoFileRef = useRef<HTMLInputElement>(null);

    const [activateVideo, setActivateVideo] = useState(false);
    const { images, setImages, isUploading, setIsUploading } =
        useMediaFilesUploadInputStore();
    const [allowPollEdit, setAllowPollEdit] = useState(true);

    const uploadPostImagesParam: UploadImagesInput = {
        images,
        setImages,
        setIsUploading,
        open: isPostFormModalOpen,
        abortController: abortController.current,
    };

    const {
        resetUpload,
        handleOnRemoveImage,
        handleDragImage,
        inappropriateContentDetected,
        failedUploadsDetected,
    } = useUploadPostCommentImages(uploadPostImagesParam);

    function triggerInput() {
        inputFileRef.current?.click();
    }

    useEffect(() => {
        setInappropriateTextError(false);
        setInappropriateImageError(false);
        setLinkoutErrorText("");
        let allImagesAreLoaded = true;
        for (const image of images) {
            if (image.isUploading) {
                allImagesAreLoaded = false;
                break;
            }
        }

        if (
            allImagesAreLoaded &&
            (images.filter((image) => !image.delete).length > 0 ||
                isPostTextValid)
        ) {
            setFormActive(true);
            setErrorText("");
        } else {
            setFormActive(false);
            if (postText.length === 1) {
                setErrorText("Mind. 2 Zeichen erforderlich");
            } else {
                setErrorText("");
            }
            setIsQuestion(data?.status?.isQuestion || false);
        }
    }, [data, images, postText, isPostTextValid, linkoutUrl]);

    useEffect(() => {
        setIsPostTextValid(postText.length > 1);
    }, [postText]);

    useEffect(() => {
        if (isPostFormModalOpen) {
            setPostText(data?.postData?.content || "");
            setImages(
                data?.postData?.images?.map((image: PostImage) => ({
                    id: image.id,
                    imageUrl: image.url,
                    imageFile: undefined,
                    delete: false,
                    isUploading: false,
                    isInappropriate: false,
                    failed: false,
                    uploaded: false,
                    mediaType: "image",
                })) || images,
            );
            if (data?.postData?.poll?.pollOptions.length > 0) {
                data?.postData.poll?.pollOptions.map((pollOption) => {
                    if (pollOption.responsesCount > 0) {
                        setAllowPollEdit(false);
                        return;
                    }
                });
                if (data?.postData?.poll?.type === PollTypesEnum.POLL_IMAGES) {
                    setPollImages(
                        data?.postData?.poll?.pollOptions?.map(
                            (pollOption) => ({
                                id: pollOption.id,
                                imageUrl: pollOption.content,
                                imageFile: undefined,
                                delete: false,
                                isUploading: false,
                                isInappropriate: false,
                                failed: false,
                                uploaded: false,
                                mediaType: "image",
                            }),
                        ),
                    );
                } else {
                    data?.postData?.poll?.pollOptions.map((pollOption) => {
                        addPollOption(pollOption);
                    });
                }

                updatePollType(data.postData.poll?.type);
            }
            setIsQuestion(data?.status?.isQuestion || false);
            if (initialUserLocation) {
                setUserLocation(initialUserLocation);
            } else {
                setUserLocation({
                    id: null,
                    type: "",
                    description: "",
                    icon: null,
                    latitude: data?.postData?.geoLat?.toString(),
                    longitude: data?.postData?.geoLong?.toString(),
                    headline: data?.postData?.location || "",
                });
            }
        }
    }, [isPostFormModalOpen]);

    useEffect(() => {
        initialUserLocation && setUserLocation(initialUserLocation);
    }, [initialUserLocation, isPostFormModalOpen]);

    useEffect(() => {
        setInitialUserLocation?.(userLocation);
    }, [userLocation]);

    useEffect(() => {
        if (data?.postData?.locationNew) {
            setUserLocation({
                type: "",
                id: data.postData.locationNew.id,
                latitude: data.postData.locationNew.geoLat,
                longitude: data.postData.locationNew.geoLong,
                headline: data.postData.locationNew.name,
                description: data.postData.locationNew.subline,
                icon: null,
            });
        }
    }, [data]);

    const [selectedConversations, setSelectedConversations] = useState<
        Array<string>
    >([]);

    const { mainGroupId } = useGroupSelector();

    function onConversationSelect(key: string) {
        if (selectedConversations.includes(key)) {
            setSelectedConversations((prevState) =>
                prevState.filter((item) => item !== key),
            );
            return;
        }
        setSelectedConversations((prevState) => [...prevState, key]);
    }

    const submitButtonProps: ButtonProps = {
        variant: "contained",
        disableElevation: true,
        disabled: !formActive || isSubmitting,
        size: "large",
        onClick: (e) => {
            NewPostSubmitHandler(e);
        },
    };

    const prefetchPoiLocationId = (locationId?: string) => {
        return locationId?.includes(GOOGLE_PLACE_PREFIX)
            ? getPoi(locationId).then(
                  (response: PointOfInterestResponse) => response.id,
              )
            : Promise.resolve(locationId);
    };

    const { images: pollImages, setImages: setPollImages } =
        usePostFormPollMediaFilesUploadInputStore();

    const submitCallback = () => {
        if (linkoutUrl.length > 0 && !isValidUrl(linkoutUrl)) {
            setLinkoutErrorText("Ungültiger Link");
            setIsSubmitting(false);
            return;
        }

        const imagesToSave = images.filter(
            (image) => image.id || (image.key && image.bucket),
        );

        const pollImagesToSave = pollImages.filter(
            (image) => image.id || (image.key && image.bucket),
        );

        const pollOptionsToSubmit = pollOptionsFormIsActive
            ? pollType === PollTypesEnum.POLL_IMAGES
                ? pollImagesToSave.map((image, key) => ({
                      content:
                          image.key !== undefined ? image.key : image.imageUrl,
                      id: image?.id,
                      isDeleted: image.delete ?? false,
                      order: key + 1,
                  }))
                : pollOptions
                      .filter(
                          (item) =>
                              (item.persistedId || !item.isDeleted) &&
                              item.content.length,
                      )
                      .map((item) => {
                          return {
                              ...item,
                              id: item.persistedId,
                          };
                      })
            : [];

        prefetchPoiLocationId(userLocation?.id as string)
            .then((locationId?: string) => {
                const postData = {
                    content: postText,
                    linkoutUrl: formatLink(linkoutUrl),
                    pollOptions: pollOptionsToSubmit,
                    pollType: pollOptionsFormIsActive ? pollType : undefined,
                    pollClosedAt: closedAt
                        ? dayjs().add(closedAt, "h").format("")
                        : undefined,
                    location: userLocation?.headline,
                    locationId,
                    isQuestion: isQuestion,
                    geoLat: userLocation?.latitude,
                    geoLong: userLocation?.longitude,
                };

                if (data && data.id) {
                    return putGroupPost(data.id.toString(), {
                        ...postData,
                        imageList: imagesToSave,
                    });
                }
                switch (postCreationTarget) {
                    case POST_CREATION_TARGET.POI:
                        return createLocationPost(
                            extractLocationId(locationId as string),
                            {
                                ...postData,
                                imageList: imagesToSave,
                                sourceType: newPostSourceType,
                            },
                        );
                    case POST_CREATION_TARGET.GROUP:
                        return (
                            mainGroupId &&
                            postGroupPost(mainGroupId, {
                                ...postData,
                                imageList: imagesToSave,
                                repostToChats: !pollOptionsFormIsActive
                                    ? selectedConversations
                                    : [],
                                sourceType: newPostSourceType,
                            })
                        );
                }
            })
            .then((createdPostResponse) => {
                postFormSubmissionCallback?.(data, createdPostResponse);
                publish({ name: POST_SUBMITTED });
                handleClose();
                setIsSubmitting(false);
            })
            .catch((err) => {
                switch (err.response?.status) {
                    case RESPONSE_CODE_NOT_AUTHORIZED:
                        break;
                    case RESPONSE_CODE_INAPPROPRIATE_TEXT:
                        setInappropriateTextError(true);
                        setIsSubmitting(false);
                        break;
                    case RESPONSE_CODE_RESOURCE_SUSPENDED:
                    case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                        setErrorState(ErrorState.POST_SUSPENDED);
                        setIsSubmitting(false);
                        handleClose();
                        break;
                    case RESPONSE_CODE_PROFILE_SUSPENDED:
                        setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                        setIsSubmitting(false);
                        handleClose();
                        break;
                    case RESPONSE_CODE_LIMIT_EXCEEDED:
                        setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                        setIsSubmitting(false);
                        handleClose();
                        break;
                    default:
                        if (err.final === undefined) {
                            setPostCreationErrorModalOpen(true);
                            setIsSubmitting(false);
                        }
                }
            });
    };

    const cancelCallback = () => {
        setIsSubmitting(false);
        handleClose();
    };

    useInterruptableAction(isSubmitting, submitCallback, cancelCallback);

    const { setErrorState } = useErrorStore();

    function NewPostSubmitHandler(event: any) {
        event.preventDefault();
        setIsSubmitting(true);
        setUserAuthContextState(UserLoginActionState.CREATE_POST);
        submitCallback();
    }

    function handlePostTextChange(event: any) {
        const textVal: string = event?.target.value;
        setPostText(textVal);
    }

    function onIsQuestionChangeHandler(event: any) {
        setIsQuestion(event.target.checked);
    }

    const handleTabChange = (event, newTabIndex) => {
        setTabIndex(newTabIndex);
    };
    const triggerLocationInputStackClick = () => {
        setTabIndex(POST_FORM_TABS.LOCATION);
    };

    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            const minHeight = 40;
            inputRef.current.style.height = `${minHeight}px`;
            let scrollHeight = inputRef.current.scrollHeight;
            if (scrollHeight < minHeight) {
                scrollHeight = minHeight;
            }
            inputRef.current.style.height = addPxPostfix(scrollHeight);
        }
    }, [inputRef.current, postText, tabIndex]);

    const [inappropriateTextError, setInappropriateTextError] = useState(false);
    const [inappropriateImageError, setInappropriateImageError] =
        useState(false);
    const [errorText, setErrorText] = useState("");
    const [linkoutErrorText, setLinkoutErrorText] = useState("");
    const { setUserAuthContextState } = useUserStore();

    const groupTypeLocal = data?.postData ? data?.group?.type : groupType;
    const isAskCommunityCheckboxHidden =
        !formActive ||
        images.length ||
        linkoutUrl.length ||
        !(
            groupTypeLocal === GroupTypeEnum.HOTEL ||
            groupTypeLocal === GroupTypeEnum.HOLIDAY_HOME
        );

    useEffect(() => {
        if (isAskCommunityCheckboxHidden) {
            setIsQuestion(false);
        }
    }, [isAskCommunityCheckboxHidden]);

    const pollOptionsFormIsActive = isPostTextValid && isPollOptionsValid;

    useEffect(() => {
        if (!isPostFormModalOpen) {
            return;
        }

        if (postFormOpeningTarget === POST_FORM_CONTENT_INPUT.LOCATION_INPUT) {
            setTabIndex(POST_FORM_TABS.LOCATION);
        } else if (
            postFormOpeningTarget === POST_FORM_CONTENT_INPUT.POLL_INPUT
        ) {
            setTabIndex(POST_FORM_TABS.POLL);
        } else if (
            postFormOpeningTarget === POST_FORM_CONTENT_INPUT.LINKOUT_URL_INPUT
        ) {
            setTabIndex(POST_FORM_TABS.LINKOUT);
        }
    }, [isPostFormModalOpen, postFormOpeningTarget]);

    useEffect(() => {
        if (tabIndex === POST_FORM_TABS.DEFAULT && inputRef.current) {
            inputRef.current.focus();
        }
    }, [tabIndex, inputRef.current]);

    useEffect(() => {
        if (inappropriateTextError) {
            setErrorText(
                "Dein Textbeitrag wurde aufgrund unserer Richtlinien leider abgelehnt.",
            );
        } else if (inappropriateImageError) {
            setErrorText(
                "Dein Bildbeitrag wurde aufgrund unserer Richtlinien leider abgelehnt.",
            );
        } else if (failedUploadsDetected) {
            setErrorText(
                "Bilder Upload fehlgeschlagen. Bitte versuche es erneut.",
            );
        } else {
            setErrorText("");
        }
    }, [
        inappropriateTextError,
        inappropriateImageError,
        failedUploadsDetected,
    ]);

    const hideInputContainerButtons =
        images.length > 0 &&
        userLocation !== undefined &&
        userLocation.headline !== "" &&
        (!showLinkoutInput || linkoutUrl.length > 0) &&
        pollOptionsFormIsActive;

    useEffect(() => {
        const handleTouchMove = () => {
            inputRef.current?.blur();
        };

        window.addEventListener("touchmove", handleTouchMove, {
            passive: true,
        });
        return () => {
            window.removeEventListener("touchmove", handleTouchMove);
            handleClose();
        };
    }, []);

    return (
        <ModalConstructor
            onClose={handleClose}
            open={isPostFormModalOpen}
            modalContent={
                <TabsStyled value={tabIndex} onChange={handleTabChange}>
                    <form style={{ height: "100%" }}>
                        {!isSubmitting &&
                            tabIndex === POST_FORM_TABS.DEFAULT && (
                                <FormContainerStack>
                                    <PostFormHeader />
                                    <PostTextOutlinedInput
                                        className={
                                            errorText.length ? "_error" : ""
                                        }
                                        sx={{
                                            border: isPostTextValid
                                                ? "1px solid var(--community-color-dark-gray)"
                                                : "1px solid var(--community-color-transparent-black-05)",
                                        }}
                                        placeholder={createPostPlaceholder}
                                        name="postText"
                                        autoFocus={true}
                                        rows={1}
                                        value={postText}
                                        ref={inputRef}
                                        onFocus={(e) => {
                                            e.currentTarget.setSelectionRange(
                                                e.currentTarget.value.length,
                                                e.currentTarget.value.length,
                                            );
                                        }}
                                        onInput={handlePostTextChange}
                                    />
                                    {errorText && (
                                        <ErrorContainer>
                                            {errorText}
                                        </ErrorContainer>
                                    )}
                                    {!pollOptionsFormIsActive && (
                                        <CheckboxFormControlLabel
                                            control={
                                                <Stack
                                                    flexDirection="row"
                                                    alignItems="center"
                                                >
                                                    <IsQuestionCheckbox
                                                        checked={isQuestion}
                                                        onChange={
                                                            onIsQuestionChangeHandler
                                                        }
                                                        name="isQuestion"
                                                    />
                                                </Stack>
                                            }
                                            label={
                                                <span>
                                                    Du möchtest deine{" "}
                                                    <BoldText>
                                                        Frage zur Unterkunft
                                                    </BoldText>{" "}
                                                    auch an{" "}
                                                    <BoldText>
                                                        ehemalige Gäste
                                                    </BoldText>{" "}
                                                    schicken?
                                                </span>
                                            }
                                            isHidden={
                                                isAskCommunityCheckboxHidden
                                            }
                                        />
                                    )}
                                    {pollOptionsFormIsActive && (
                                        <PostPollOptionsPreview
                                            handleDeleteSelectedPollOptions={() => {
                                                clearPollOptions();
                                                updatePollType(
                                                    PollTypesEnum.POLL_OFF,
                                                );
                                            }}
                                            onContainerClick={() => {
                                                setTabIndex(
                                                    POST_FORM_TABS.POLL,
                                                );
                                            }}
                                        />
                                    )}
                                    <ImagesPreview
                                        triggerInput={triggerInput}
                                        handleOnRemoveImage={
                                            handleOnRemoveImage
                                        }
                                        images={images}
                                        isHidden={
                                            images.filter(
                                                (image) => !image.delete,
                                            ).length === 0
                                        }
                                        handleDragImage={handleDragImage}
                                        handleImageUploadRetry={resetUpload}
                                        inappropriateContentDetected={
                                            inappropriateContentDetected
                                        }
                                        failedUploadsDetected={
                                            failedUploadsDetected
                                        }
                                    />
                                    {!linkoutErrorText &&
                                        showLinkoutInput &&
                                        linkoutUrl.length > 0 && (
                                            <Box style={{ marginTop: "12px" }}>
                                                <SelectedLinkoutPreview
                                                    setTabIndex={setTabIndex}
                                                    linkoutUrl={linkoutUrl}
                                                    handleDeleteLinkout={() => {
                                                        clearLinkout();
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    {linkoutErrorText && showLinkoutInput && (
                                        <ErrorContainer>
                                            {linkoutErrorText}
                                        </ErrorContainer>
                                    )}
                                    <InputsContainer
                                        sx={{
                                            display: hideInputContainerButtons
                                                ? "none"
                                                : "flex",
                                        }}
                                    >
                                        <InputsContainerTitleTypography>
                                            Füge deinem Beitrag noch etwas hinzu
                                        </InputsContainerTitleTypography>
                                        <OptionsContainer>
                                            <ImagesFormControlLabel
                                                sx={{
                                                    display:
                                                        images.filter(
                                                            (image) =>
                                                                !image.delete,
                                                        ).length === 0
                                                            ? "flex"
                                                            : "none",
                                                }}
                                                label=""
                                                control={
                                                    <FormImageInputStack
                                                        onClick={(e) => {
                                                            triggerInput(e);
                                                        }}
                                                    >
                                                        <MediaFilesUploadInput
                                                            ref={inputFileRef}
                                                            limit={20}
                                                        />
                                                        <OptionsIconButton>
                                                            <ImageSolidIcon
                                                                color={
                                                                    "var(--community-color-light-steal-blue)"
                                                                }
                                                            />
                                                            <Typography>
                                                                Foto
                                                            </Typography>
                                                        </OptionsIconButton>
                                                    </FormImageInputStack>
                                                }
                                            />
                                            {(addVideoEnabled ||
                                                activateVideo) && (
                                                <ImagesFormControlLabel
                                                    sx={{
                                                        display:
                                                            images.filter(
                                                                (image) =>
                                                                    !image.delete,
                                                            ).length === 0
                                                                ? "flex"
                                                                : "none",
                                                    }}
                                                    label=""
                                                    control={
                                                        <FormImageInputStack
                                                            onClick={() => {
                                                                inputVideoFileRef.current?.click();
                                                            }}
                                                        >
                                                            <MediaFilesUploadInput
                                                                ref={
                                                                    inputVideoFileRef
                                                                }
                                                                limit={20}
                                                                mediaType={
                                                                    MEDIA_TYPE.VIDEO
                                                                }
                                                            />
                                                            <OptionsIconButton>
                                                                <PlaySolidIcon
                                                                    color={
                                                                        "var(--community-color-light-steal-blue)"
                                                                    }
                                                                />
                                                                <Typography>
                                                                    Video
                                                                </Typography>
                                                            </OptionsIconButton>
                                                        </FormImageInputStack>
                                                    }
                                                />
                                            )}
                                            {postCreationTarget !==
                                                POST_CREATION_TARGET.POI &&
                                                addLocationEnabled &&
                                                (userLocation === undefined ||
                                                    userLocation.headline ===
                                                        "") && (
                                                    <PostFormInputTriggerButton
                                                        onClick={
                                                            triggerLocationInputStackClick
                                                        }
                                                    >
                                                        <LocationDotSolidIcon
                                                            color={
                                                                "var(--community-color-primary-text)"
                                                            }
                                                        />
                                                        <Typography>
                                                            Ort
                                                        </Typography>
                                                    </PostFormInputTriggerButton>
                                                )}
                                            {!pollOptionsFormIsActive &&
                                                addPollEnabled && (
                                                    <PostFormInputTriggerButton
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            updatePollType(
                                                                PollTypesEnum.POLL_HIDDEN,
                                                            );
                                                            setTabIndex(
                                                                POST_FORM_TABS.POLL,
                                                            );
                                                        }}
                                                    >
                                                        <SquarePollSolidIcon
                                                            color={
                                                                "var(--community-color-primary-text)"
                                                            }
                                                        />
                                                        <Typography>
                                                            Umfrage
                                                        </Typography>
                                                    </PostFormInputTriggerButton>
                                                )}
                                            {showLinkoutInput &&
                                                linkoutUrl.length === 0 && (
                                                    <HeadlinePostInputButton
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setTabIndex(
                                                                POST_FORM_TABS.LINKOUT,
                                                            );
                                                        }}
                                                    >
                                                        <LinkSolidIcon
                                                            color={
                                                                "var(--community-color-primary-text)"
                                                            }
                                                        />
                                                        <Typography>
                                                            Link
                                                        </Typography>
                                                    </HeadlinePostInputButton>
                                                )}
                                        </OptionsContainer>
                                    </InputsContainer>
                                    {!pollOptionsFormIsActive && !data?.id && (
                                        <UserConversationListFacade
                                            onConversationSelect={
                                                onConversationSelect
                                            }
                                            selectedConversations={
                                                selectedConversations
                                            }
                                        />
                                    )}
                                </FormContainerStack>
                            )}
                        {!isSubmitting &&
                            tabIndex === POST_FORM_TABS.LINKOUT && (
                                <FormContainerStack>
                                    <LinkoutFieldTab
                                        errorText={errorText}
                                        linkoutRef={linkoutRef}
                                        linkoutUrl={linkoutUrl}
                                        setTabIndex={setTabIndex}
                                        setLinkoutUrl={setLinkoutUrl}
                                    />
                                </FormContainerStack>
                            )}
                        {!isSubmitting && tabIndex === POST_FORM_TABS.POLL && (
                            <Stack style={{ padding: "12px 16px" }}>
                                <PollTab
                                    setPostText={setPostText}
                                    postText={postText}
                                    allowPollEdit={allowPollEdit}
                                />
                            </Stack>
                        )}
                        {!isSubmitting &&
                            tabIndex === POST_FORM_TABS.LOCATION && (
                                <LocationSearchTab
                                    setUserLocation={setUserLocation}
                                    setTabIndex={setTabIndex}
                                />
                            )}
                        {!isSubmitting &&
                            tabIndex === POST_FORM_TABS.DEFAULT && (
                                <SubmitStack
                                    sx={{
                                        margin: hideInputContainerButtons
                                            ? "16px 16px 0 16px"
                                            : "0 16px",
                                    }}
                                >
                                    <PostFormInfoText
                                        onLastWordDoubleClick={() => {
                                            setActivateVideo(true);
                                        }}
                                    />
                                </SubmitStack>
                            )}
                        {isSubmitting && (
                            <LoadingStack>
                                <CommunityCircularProgress />
                            </LoadingStack>
                        )}
                    </form>
                    <InvalidMediaUploadModal />
                    <PostCreationErrorModal
                        open={isPostCreationErrorModalOpen}
                        setOpen={setPostCreationErrorModalOpen}
                    />
                </TabsStyled>
            }
            modalTitle={
                <PostFormHeaderContainer
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                >
                    <Stack direction="row" alignItems="center" gap="8px">
                        {tabIndex !== POST_FORM_TABS.DEFAULT && (
                            <IconButton
                                onClick={() =>
                                    setTabIndex(POST_FORM_TABS.DEFAULT)
                                }
                                style={{
                                    marginLeft: "-8px",
                                }}
                            >
                                <ArrowLeftIcon />
                            </IconButton>
                        )}
                        <FormHeader data-testid="post-form-modal-header">
                            {getModalTitle(tabIndex)}
                        </FormHeader>
                    </Stack>
                    {formActive &&
                        !isSubmitting &&
                        !isUploading &&
                        tabIndex === POST_FORM_TABS.DEFAULT && (
                            <SubmitButton {...submitButtonProps}>
                                Veröffentlichen
                            </SubmitButton>
                        )}
                    {pollOptionsFormIsActive &&
                        tabIndex === POST_FORM_TABS.POLL && (
                            <SubmitButton
                                variant={"contained"}
                                disableElevation={true}
                                size={"large"}
                                onClick={() => {
                                    setTabIndex(POST_FORM_TABS.DEFAULT);
                                }}
                            >
                                Speichern
                            </SubmitButton>
                        )}
                </PostFormHeaderContainer>
            }
            isBorderRadiusVisible={true}
            isInsideClosingButtonVisible={tabIndex === POST_FORM_TABS.DEFAULT}
            modalInnerContainerStyles={{
                overflowY: "unset",
                height: "calc(100vh - 24px)",
                maxWidth: "640px",
                borderRadiusTopLeft: "12px",
                borderRadiusTopRight: "12px",
                "@media (min-width: 640px)": {
                    borderRadius: "12px",
                },
                "& .MuiDialogContent-root": {
                    marginTop: "0",
                    paddingBottom: "var(--safe-area-inset-bottom)",
                },
                "& .MuiDialogActions-root": {
                    justifyContent: "flex-start",
                    margin: "16px",
                },
                "& .MuiDialogTitle-root": {
                    margin: 0,
                    padding: "12px 16px",
                },
            }}
        />
    );
}

function getModalTitle(tabIndex = POST_FORM_TABS.DEFAULT) {
    if (tabIndex === POST_FORM_TABS.LOCATION) {
        return "Ort hinzufügen";
    } else if (tabIndex === POST_FORM_TABS.LINKOUT) {
        return "Link hinzufügen";
    } else if (tabIndex === POST_FORM_TABS.POLL) {
        return "Umfrage erstellen";
    }
    return "Schreibe etwas";
}
